/**
 * @file Entry point declarativo de criação de componentes do EP.
 * @module components/ep
 *
 * `create()` é a principal API pública do EP. Recebe uma configuração ou uma
 * lista de configurações, resolve defaults, componentes especiais, composites e
 * relações por `ident`, e devolve o elemento/controlador criado.
 */

import createElement from "./create/element";

/**
 * Orquestrador interno da API `create()`.
 *
 * @class EP
 */
class EP {
  elems = {};
  countElems = 0;

  /**
   * Carrega o módulo de defaults de um elemento.
   *
   * Quando não existe módulo EP específico, retorna um default genérico com
   * `tag` igual ao nome solicitado.
   *
   * @param {string} element Nome declarativo do elemento.
   * @returns {Promise<Object>} Módulo carregado ou fallback genérico.
   * @throws {Error} Repassa erros de importação que não representem módulo ausente.
   */
  async #loadModule(element) {
    try {
      return await import(`./default/${element}`);
    } catch (error) {
      if (error.message.includes('Cannot find module')) {
        return { default: { tag: element } };
      } else {
        throw error; // Relançar o erro se for outro tipo de problema
      }
    }
  }

  /**
   * Carrega um componente especial de `create/` e executa sua factory default.
   *
   * Se o módulo especial não puder ser usado, delega ao carregamento normal de
   * defaults. O contexto dinâmico é restrito a JavaScript para impedir que
   * arquivos de documentação sejam tratados como módulos da aplicação.
   *
   * @param {Object} opt Configuração declarativa.
   * @returns {Promise<*>} Resultado do componente especial ou módulo fallback.
   * @see EP#loadModule
   */
  async #loadSpecial(opt) {
    try {
      // This dynamic path is used for legacy special elements. Restrict its
      // webpack context to JavaScript modules so documentation files in the
      // same folder can never be parsed as application code.
      const exp = await import(
        /* webpackInclude: /\.js$/ */
        `./create/${opt.element}`
      );
      return exp.default(opt);
    } catch (error) {
      return await this.#loadModule(opt.element);
    }
  }

  /**
   * Resolve `parent: ':ident'` para o elemento previamente armazenado.
   *
   * @param {Object} [options={}] Opções declarativas.
   * @returns {Object} Cópia com parent resolvido quando aplicável.
   */
  #resolveIdentParent(options = {}) {
    const resolved = { ...options };
    if (
      resolved.parent !== undefined
      && typeof resolved.parent === 'string'
      && resolved.parent.substr(0, 1) === ':'
    ) {
      const key = resolved.parent.substr(1);
      resolved.parent = this.elems[key];
    }
    return resolved;
  }

  /**
   * Armazena um composite no registry de `ident` da execução corrente.
   *
   * @param {*} elem Elemento/controlador criado.
   * @param {Object} [options={}] Opções usadas na criação.
   * @returns {void}
   */
  #storeComposite(elem, options = {}) {
    const ident = (options.ident !== undefined && typeof options.ident === 'string')
      ? options.ident
      : (`ident-${++this.countElems}`);
    this.elems[ident] = elem;
  }

  /**
   * Cria um ou vários componentes EP a partir de configuração declarativa.
   *
   * Um objeto único é convertido internamente em lista. Em listas, elementos
   * podem receber `ident` e os seguintes podem usar `parent: ':ident'`.
   *
   * Tratamentos especiais atuais incluem:
   * - `form` com `isDialog: true`, convertido em Dialog contendo o Form;
   * - composites `datetime`, `geography`, `geodoc` e `address`;
   * - `palette` e `iconPicker`;
   * - `menu` e o legado `dual`;
   * - demais elementos via defaults + `createElement()`.
   *
   * Ao final, instala handlers globais de fechamento de Combo/calendário e o
   * handler de scroll compartilhado dos Combos.
   *
   * @param {Object|Object[]} list Configuração ou lista de configurações EP.
   * @returns {Promise<*|Object<string,*>>} Elemento único, controller especial ou registry de elementos da lista.
   *
   * @example
   * await create({
   *   element: 'text',
   *   parent: '#form',
   *   name: 'display_name',
   *   label: 'Nome'
   * });
   *
   * @example
   * await create([
   *   { element: 'div', parent: '#screen', ident: 'panel' },
   *   { element: 'text', parent: ':panel', name: 'name', label: 'Nome' }
   * ]);
   *
   * @see createElement
   * @see module:components/create/geography/geography
   * @see module:components/create/geodoc
   */
  async create(list) {
    if (!Array.isArray(list)) {
      list = [list];
    }

    /**
     * Substitui in-place o conteúdo de um objeto preservando sua referência.
     *
     * @param {Object} target Objeto que mantém a identidade.
     * @param {Object} source Novo conteúdo.
     * @returns {void}
     */
    const replaceObject = (target, source) => {
      for (const key of Object.keys(target)) {
        delete target[key];
      }
      Object.assign(target, source);
    };

    for (const opt of list) {
      if (opt.element !== undefined && typeof opt.element === 'string') {
        if (opt.element === 'form' && opt.isDialog === true) {
          const {
            parent: formParent,
            dialog: dialogOverrides = {},
            title,
            buttons,
            autoOpen,
            kill,
            width,
            height,
            isDialog,
            ...formOptions
          } = opt;

          const newFormOptions = {
            ...formOptions,
            element: 'form'
          };

          delete newFormOptions.isDialog;
          delete newFormOptions.parent;
          if (newFormOptions.title !== undefined) {
            delete newFormOptions.title;
          }

          const baseDialogConfig = {
            modal: true,
            effect: 'zoom',
            autoOpen: true,
            kill: false,
            ...dialogOverrides
          };

          if (title !== undefined) baseDialogConfig.title = title;
          if (buttons !== undefined) baseDialogConfig.buttons = buttons;
          if (autoOpen !== undefined) baseDialogConfig.autoOpen = autoOpen;
          if (kill !== undefined) baseDialogConfig.kill = kill;
          if (width !== undefined) baseDialogConfig.width = width;
          if (height !== undefined) baseDialogConfig.height = height;

          const dialogOption = {
            element: 'div',
            parent: formParent ?? document.body,
            dialog: baseDialogConfig,
            isGrid: true,
            content: [newFormOptions]
          };

          replaceObject(opt, dialogOption);
        }

        const specialList = ['dual'];

        if (opt.element === 'datetime') {
          const { default: datetime } = await import('./create/datetime/datetime');
          const elem = await datetime(opt);

          if (list.length === 1) {
            return elem;
          }

          const ident = (opt.ident !== undefined && typeof opt.ident === 'string') ? (opt.ident) : (`ident-${++this.countElems}`);
          this.elems[ident] = elem;
        } else if (opt.element === 'geography') {
          const { default: geography } = await import('./create/geography/geography');
          const geographyOptions = this.#resolveIdentParent(opt);
          const [elem] = await geography(geographyOptions);

          if (list.length === 1) {
            return elem;
          }

          this.#storeComposite(elem, geographyOptions);
        } else if (opt.element === 'geodoc') {
          const { default: geodoc } = await import('./create/geodoc');
          const geodocOptions = this.#resolveIdentParent(opt);
          const elem = await geodoc(geodocOptions);

          if (list.length === 1) {
            return elem;
          }

          this.#storeComposite(elem, geodocOptions);
        } else if (opt.element === 'address') {
          const { default: address } = await import('./create/address');
          const addressOptions = this.#resolveIdentParent(opt);
          const elem = await address(addressOptions);

          if (list.length === 1) {
            return elem;
          }

          this.#storeComposite(elem, addressOptions);
        } else if (['colorPicker', 'colorpicker'].includes(opt.element)) {
          throw new Error('EP_COLORPICKER_MUST_BE_INSIDE_PALETTE');
        } else if (['palette', 'iconPicker', 'iconpicker'].includes(opt.element)) {
          const moduleName = opt.element === 'palette'
            ? 'palette'
            : 'iconPicker';
          // Keep the dynamic import bounded. Without an explicit map webpack
          // creates a context for every file in create/, including PICKERS.md.
          const pickerLoaders = {
            palette: () => import('./create/palette'),
            iconPicker: () => import('./create/iconPicker'),
          };
          const module = await pickerLoaders[moduleName]();
          const componentOptions = this.#resolveIdentParent({ ...opt, element: moduleName });
          const [elem, resolvedOptions] = await module.default(componentOptions);
          const target = resolvedOptions?.parent;
          if (elem && target && !elem.isConnected) {
            target.append(elem);
          }

          if (list.length === 1) {
            return elem;
          }

          this.#storeComposite(elem, componentOptions);
        } else if (opt.element === 'menu') {
          const controller = await this.#loadSpecial(opt);
          if (list.length === 1) {
            return controller;
          }
        } else if (specialList.indexOf(opt.element) >= 0) {
          let op = await this.#loadSpecial(opt);
          if (op.default !== undefined) {
            op.default[0] = { ...opt, ...op.default[0] };
            create(op.default);
          } else {
            create(op);
          }
        } else {
          let options, op = await this.#loadModule(opt.element);

          if (opt.element === 'dialog') {
            options = {
              ...op.default,
              ...Object.fromEntries(Object.entries(opt).filter(([_, v]) => v !== undefined)),
              dialog: {
                ...(op.default.dialog || {}),
                ...(opt.dialog || {})
              }
            };
          } else {
            options = {
              ...op.default,
              ...Object.fromEntries(Object.entries(opt).filter(([_, v]) => v !== undefined))
            };
          }

          if (options.parent !== undefined && typeof options.parent === 'string' && options.parent.substr(0, 1) === ':') {
            const key = options.parent.substr(1);
            options.parent = this.elems[key];
          }

          const elem = await createElement(options);

          if (list.length === 1) {
            return elem;
          }

          const ident = (options.ident !== undefined && typeof options.ident === 'string') ? (options.ident) : (`ident-${++this.countElems}`);
          this.elems[ident] = elem;
        }
      }
    }

    document.addEventListener('click', (e) => {
      document.querySelectorAll('.combo').forEach(item => {
        const id = item.dataset.opts;
        const opts = document.querySelector(`.combo-options[data-id="${id}"]`);

        const isInsideCombo = item.contains(e.target);
        const isInsideOptions = opts?.contains(e.target);

        if (!isInsideCombo && !isInsideOptions) {
          if (opts && typeof opts.closeCombo === 'function') {
            opts.closeCombo();
          } else {
            item.classList.remove('open-up', 'open-down');
            item.classList.add('closed');
            opts?.classList.remove('cb-up', 'cb-down');
            opts?.classList.add('hidden');
          }
        }
      });

      // Verifica se o clique foi no campo de entrada ou no calendário
      const isCalendar = e.target.closest('.calendar');
      const isInput = e.target.closest('input[data-type="date"]');

      if (isCalendar || isInput) {
        // Impede o fechamento se o clique for no calendário ou no campo
        return;
      }

      // Caso contrário, remove todos os calendários
      document.querySelectorAll('.calendar').forEach(calendar => calendar.remove());
    });

    if (!window.__epComboScrollHandler) {
      /**
       * Fecha Combos em scroll externo, ignorando scroll originado no próprio
       * painel de opções.
       *
       * @param {Event} event Evento de scroll capturado.
       * @returns {void}
       */
      const handleScroll = (event) => {
        const target = event.target;
        if (target?.closest && target.closest('.combo-options')) {
          return;
        }
        closeCombos();
      };
      window.__epComboScrollHandler = handleScroll;
      window.addEventListener('scroll', handleScroll, true);
    }

    if (list.length === 1) {
      return this.elems[0];
    }
    return this.elems;
  }
}

/**
 * Fecha todos os Combos abertos usando sua API runtime quando disponível.
 *
 * @returns {void}
 */
function closeCombos() {
  document.querySelectorAll('.combo').forEach(item => {
    const id = item.dataset.opts;
    const opts = document.querySelector(`.combo-options[data-id="${id}"]`);
    if (opts && typeof opts.closeCombo === 'function') {
      opts.closeCombo();
    } else {
      item.classList.remove('open-up', 'open-down');
      item.classList.add('closed');
      opts?.classList?.remove('cb-up', 'cb-down');
      opts?.classList?.add('hidden');
    }
  });
}

const ep = new EP();

/**
 * API pública global de criação declarativa do EP.
 *
 * A mesma função é exportada como default e publicada em `window.create` para
 * compatibilidade com componentes internos que dependem do runtime global.
 *
 * @param {Object|Object[]} list Configuração ou lista de configurações.
 * @returns {Promise<*>} Resultado de `EP#create`.
 * @see EP#create
 */
const create = (list) => ep.create(list);
window.create = create;
export default create;
